import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { SUIT_NAME, SUIT_SYMBOL, type Suit } from '../game/match';
import { avatarUrl } from '../lib/api';
import { dismiss, useToasts } from '../lib/toast';

// ---------------------------------------------------------------------------
// Avatar & names
// ---------------------------------------------------------------------------

function hashHue(s: string): number {
  let h = 0;
  for (const ch of s) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return h % 360;
}

/** Stable fallback-avatar colour for a name, used only before a player has a team. */
export const avatarColor = (name: string) => `hsl(${hashHue(name || '?')} 52% 34%)`;

/**
 * Deepens a hex team colour into a readable avatar-fill shade (dark enough for
 * white initials, but still clearly "that team's colour") instead of a colour
 * that's unrelated to the ring around it.
 */
export function teamAvatarFill(hex: string): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const darken = (h: string) => Math.round(parseInt(h, 16) * 0.55);
  return `rgb(${darken(m[1]!)} ${darken(m[2]!)} ${darken(m[3]!)})`;
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  // Array.from keeps surrogate pairs / Urdu letters intact.
  const first = (w?: string) => (w ? Array.from(w)[0] ?? '' : '');
  const out = words.length > 1 ? first(words[0]) + first(words[words.length - 1]) : Array.from(words[0] ?? '?').slice(0, 2).join('');
  return out.toLocaleUpperCase() || '?';
}

interface AvatarProps {
  name: string;
  avatarKey?: string | null;
  /** Local preview (object URL) shown before/instead of the uploaded image. */
  src?: string | null;
  size?: number;
  ring?: string;
  /** Fallback-initials background; pass the player's team colour so it matches `ring`. Defaults to a per-name hash. */
  fill?: string;
  className?: string;
}

export function Avatar({ name, avatarKey, src, size = 48, ring, fill, className = '' }: AvatarProps) {
  const url = src ?? (avatarKey ? avatarUrl(avatarKey) : null);
  const style = {
    width: size,
    height: size,
    boxShadow: ring ? `0 0 0 3px ${ring}, 0 0 0 5px rgb(0 0 0 / 0.25)` : undefined,
  };
  if (url) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        className={`shrink-0 rounded-full bg-cream-dim object-cover ${className}`}
        style={style}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={`inline-grid shrink-0 place-items-center rounded-full font-bold text-white ${className}`}
      style={{ ...style, background: fill ?? avatarColor(name), fontSize: size * 0.38 }}
    >
      <span dir="auto" className="name leading-none">
        {initials(name)}
      </span>
    </span>
  );
}

/** Renders a player name safely in either direction (Urdu or Latin). */
export function Name({ children, className = '' }: { children: string; className?: string }) {
  return (
    <bdi dir="auto" className={`name ${className}`}>
      {children}
    </bdi>
  );
}

/** `onDark` picks a red that keeps AA contrast on felt; the default suits cream cards. */
export function SuitGlyph({ suit, className = '', onDark = false }: { suit: Suit; className?: string; onDark?: boolean }) {
  const red = suit === 'H' || suit === 'D';
  const color = red ? (onDark ? 'text-red-300' : 'text-suit-red') : '';
  return (
    <span role="img" aria-label={SUIT_NAME[suit]} className={`${color} ${className}`}>
      {SUIT_SYMBOL[suit]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sheet (modal dialog, bottom sheet on phones)
// ---------------------------------------------------------------------------

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Sheet({ open, onClose, title, children, footer }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className="card m-0 mt-auto max-h-[92dvh] w-full max-w-none overflow-hidden rounded-b-none p-0 sm:m-auto sm:max-w-lg sm:rounded-2xl"
    >
      {open && (
        <div className="flex max-h-[92dvh] flex-col">
          <header className="flex items-center justify-between gap-3 border-b border-ink/10 px-5 py-3">
            <h2 id={titleId} className="font-display text-xl font-bold">
              {title}
            </h2>
            <button type="button" onClick={onClose} className="btn-outline-ink btn min-h-10 px-3" aria-label="Close">
              ✕
            </button>
          </header>
          <div className="overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="border-t border-ink/10 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Stepper (keyboard-operable spinbutton)
// ---------------------------------------------------------------------------

interface StepperProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  /** Visible label; defaults to `label`. */
  children?: ReactNode;
  tone?: string;
  size?: 'md' | 'lg';
  disabled?: boolean;
}

export function Stepper({ label, value, min, max, onChange, children, tone, size = 'md', disabled }: StepperProps) {
  const id = useId();
  const clamp = (v: number) => Math.max(min, Math.min(max, v));
  const onKey = (e: KeyboardEvent) => {
    const map: Record<string, number> = {
      ArrowUp: value + 1,
      ArrowRight: value + 1,
      ArrowDown: value - 1,
      ArrowLeft: value - 1,
      PageUp: value + 3,
      PageDown: value - 3,
      Home: min,
      End: max,
    };
    if (e.key in map) {
      e.preventDefault();
      if (!disabled) onChange(clamp(map[e.key]!));
    }
  };
  const big = size === 'lg';
  const btn = `btn-outline-ink btn ${big ? 'h-14 w-14 text-2xl' : 'h-11 w-11 text-xl'} rounded-full p-0`;
  return (
    <div className="flex items-center justify-between gap-3">
      <span id={id} className="font-semibold">
        {children ?? label}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={btn}
          onClick={() => onChange(clamp(value - 1))}
          disabled={disabled || value <= min}
          aria-label={`Decrease ${label}`}
          tabIndex={-1}
        >
          −
        </button>
        <div
          role="spinbutton"
          tabIndex={disabled ? -1 : 0}
          aria-labelledby={id}
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-disabled={disabled}
          onKeyDown={onKey}
          className={`${big ? 'w-16 text-4xl' : 'w-10 text-2xl'} rounded-lg text-center font-display font-extrabold tabular-nums`}
          style={tone ? { color: tone } : undefined}
        >
          {value}
        </div>
        <button
          type="button"
          className={btn}
          onClick={() => onChange(clamp(value + 1))}
          disabled={disabled || value >= max}
          aria-label={`Increase ${label}`}
          tabIndex={-1}
        >
          +
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle & segmented control
// ---------------------------------------------------------------------------

export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-start justify-between gap-4 py-1 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <span>
        <span className="block font-semibold">{label}</span>
        {hint && <span className="block text-sm text-ink-soft">{hint}</span>}
      </span>
      <input
        type="checkbox"
        role="switch"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden
        className="relative mt-1 h-7 w-12 shrink-0 rounded-full bg-ink/25 transition peer-checked:bg-success-600 peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-success-600 after:absolute after:top-1 after:left-1 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition peer-checked:after:translate-x-5"
      />
    </label>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  disabled,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
  disabled?: boolean;
}) {
  const name = useId();
  return (
    <fieldset disabled={disabled} className="min-w-0">
      <legend className="label">{label}</legend>
      <div className="flex rounded-xl bg-ink/8 p-1">
        {options.map((o) => (
          <label key={o.value} className="flex-1">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              className="peer sr-only"
            />
            <span className="block cursor-pointer rounded-lg px-2 py-2 text-center text-sm font-semibold text-ink-soft transition peer-checked:bg-white peer-checked:text-ink peer-checked:shadow peer-focus-visible:outline-3 peer-focus-visible:outline-gold">
              {o.label}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

export function Toasts() {
  const toasts = useToasts();
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => dismiss(t.id)}
          className={`animate-rise pointer-events-auto max-w-md rounded-xl px-4 py-3 text-left text-sm font-semibold shadow-xl ${
            t.tone === 'error' ? 'bg-red-800 text-white' : t.tone === 'success' ? 'bg-cream text-ink' : 'bg-ink text-cream'
          }`}
        >
          {t.message}
        </button>
      ))}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="grid place-items-center gap-3 py-16 text-cream/80">
      <span aria-hidden className="animate-spin text-4xl">
        ♠
      </span>
      <span>{label}…</span>
    </div>
  );
}
